import React, { useEffect, useState, useRef } from "react";
import dayjs from "dayjs";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";
import Grid from "@mui/material/Grid";
import { Box, Typography, TextField, MenuItem, Select, Button, IconButton } from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import Modal from "../../components/Modal";
import style from "./style";
import { CustomizedRadio } from "../../components/CustomRadio";
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AddIcon from '@mui/icons-material/Add';
import { getDateFormat } from "../../utils/dateFormatStore";

dayjs.extend(utc);
dayjs.extend(timezone);


const CreditsPage = ({
  creditsType,
  handleAddCreditType,
  updateCreditType,
  credits,
  handleCreditsData,
  taxCountry,
  taxPeriod,
  getYearTaxTable,
  handleFrequencyChange,
  frequency,
  handleAddTaxYear,
  deleteCreditType,
  getCredits,
  contributionType,
  handleUpdateCredits,
  getAllCreditsList,
  deleteCreditById,
  handlePostCreditBeneficiaries,
  handleGetCreditBeneficiaries,
  getCreditsTypeByDateRange
}) => {
  const [creditsOpen, setCreditsOpen] = useState(false);
  const [from, setFrom] = useState("create");
  const [editCredit, setEditCredit] = useState(null);
  const [editCreditData, setEditCreditData] = useState(null);
  const [selectedCreditType, setSelectedCreditType] = useState("");
  const [isSavingCredits, setIsSavingCredits] = useState(false);
  const [changedRows, setChangedRows] = useState({});
  const [open, setOpen] = useState(false);
  const [addFY, setAddFY] = useState(false);
  const [selectedTaxYear, setSelectedTaxYear] = useState("");
  const [opens, setOpens] = useState(false);
  const [addStart, setAddStart] = useState(null);
  const [addEnd, setAddEnd] = useState(null);
  const [frequencyChanged, setFrequencyChanged] = useState(false);
  const [newCreditType, setNewCreditType] = useState("");
  const [creditToDelete, setCreditToDelete] = useState(null);
  const [contribution, setContribution] = useState(false);
  const [selectedContributionType, setSelectedContributionType] = useState("");
  const [beneficiaryModalOpen, setBeneficiaryModalOpen] = useState(false);
  const [selectedCreditForBeneficiary, setSelectedCreditForBeneficiary] = useState(null);
  const [existingCreditBeneficiaryId, setExistingCreditBeneficiaryId] = useState(null);
  const [localNewCredits, setLocalNewCredits] = useState([]);
  const [editingRows, setEditingRows] = useState({}); // Track which rows are being edited
  const [editedValues, setEditedValues] = useState({}); // Track edited values for each row
  const [originalValues, setOriginalValues] = useState({}); // Track original values to revert if needed
  const saveButtonClickedRef = useRef(false); // Track if save button was clicked
  const role = localStorage.getItem("role");
  const [credit, setCredit] = useState(
    credits && Array.isArray(credits) && credits.length > 0
      ? credits
      : [
        {
          type: "",
          description: "",
          amount: "",
          start: dayjs().format("YYYY-MM-DD"),
          end: dayjs().format("YYYY-MM-DD"),
          includes_beneficiary: false,
          employeeCont: { type: "amount" },
          employee_amt_type: "amount",
          spouse_beneficiary_type: "",
          spouse_amt: "",
          spouse_amt_type: "amount",
          additional_beneficiary_amt: "",
          additional_beneficiary_amt_type: "amount",
        },
      ]
  );

  const countryPermissions = JSON.parse(
    localStorage.getItem("countryPermissions") || "{}"
  );

  const permissions = Object.values(countryPermissions)[0] || {};

  const canEditCredits =
    role === "SuperAdmin" ||
    permissions?.credits?.edit === true;

  const [beneficiaryRates, setBeneficiaryRates] = useState([
    { id: "employee-0", key: "Employee", mode: "amount", value: "", isEditable: false },
  ]);

  const [initialBeneficiaryRates, setInitialBeneficiaryRates] = useState([
    { id: "employee-0", key: "Employee", mode: "amount", value: "", isEditable: false },
  ]);

  const [beneficiaryErrors, setBeneficiaryErrors] = useState([
    { value: "" },
  ]);


  const sanitizeNumberInput = (raw) => {
    if (raw === "" || raw === null || raw === undefined) return "";
    const num = Number(raw);
    if (Number.isNaN(num)) return "";
    return num;
  };

  const validateBeneficiaryRow = (row) => {
    const valueNum = sanitizeNumberInput(row.value);
    if (valueNum === "") return "";
    if (valueNum < 1) return "Value must be at least 1";
    if (row.mode === "percentage" && valueNum > 100) return "Percentage must be between 0 and 100";
    return "";
  };

  const computeBeneficiaryErrors = () => beneficiaryRates.map((r) => ({
    value: validateBeneficiaryRow(r),
    type: r.isEditable && r.key !== "Employee" && (!r.key || r.key.trim() === "") ? "Type name is required" : ""
  }));

  const isFormValid = () => {
    try {
      const creditType = selectedCreditType || selectedCreditForBeneficiary?.type || "";

      if (!creditType || (typeof creditType === "string" && creditType.trim() === "")) {
        return false;
      }

      const employeeRow = beneficiaryRates.find(r => r.key === "Employee");
      if (!employeeRow) {
        return false;
      }

      const employeeValue = employeeRow.value;
      if (employeeValue === "" || employeeValue === null || employeeValue === undefined || employeeValue === 0) {
        return false;
      }

      const employeeValueNum = sanitizeNumberInput(employeeValue);
      if (employeeValueNum === "" || employeeValueNum < 1) {
        return false;
      }

      const employeeError = validateBeneficiaryRow(employeeRow);
      if (employeeError && employeeError !== "") {
        return false;
      }

      const customBeneficiaries = beneficiaryRates.filter(r => r.key !== "Employee");

      for (const row of customBeneficiaries) {
        const rowName = row.key ? row.key.trim() : "";
        const rowValue = row.value;
        const hasName = rowName !== "";
        const hasValue = rowValue !== "" && rowValue !== null && rowValue !== undefined && rowValue !== 0;

        if (hasName || hasValue) {
          if (!hasName) {
            return false;
          }
          if (rowValue === "" || rowValue === null || rowValue === undefined || rowValue === 0) {
            return false; // Row has name but missing value
          }

          const rowValueNum = sanitizeNumberInput(rowValue);
          if (rowValueNum === "" || rowValueNum < 1) {
            return false; // Invalid value
          }

          const rowError = validateBeneficiaryRow(row);
          if (rowError && rowError !== "") {
            return false;
          }
        }
      }

      const beneErrors = computeBeneficiaryErrors();
      const hasAnyBeneficiaryError = beneErrors.some(e => (e.value && e.value !== "") || (e.type && e.type !== ""));
      if (hasAnyBeneficiaryError) {
        return false;
      }

      return true;
    } catch (error) {
      return false;
    }
  };

  const handleBeneficiaryMode = (rowIndex, newMode) => {
    setBeneficiaryRates(prev => prev.map((r, i) => i === rowIndex ? { ...r, mode: newMode, value: newMode === "percentage" ? (r.value === "" ? "" : sanitizeNumberInput(r.value)) : (r.value === "" ? "" : sanitizeNumberInput(r.value)) } : r));
    setBeneficiaryErrors(prev => prev.map((e, i) => i === rowIndex ? {
      ...e,
      value: validateBeneficiaryRow({ ...beneficiaryRates[rowIndex], mode: newMode })
    } : e));
  };

  const handleBeneficiaryValue = (rowIndex, newValue) => {
    let sanitized = newValue;
    if (sanitized === "" || sanitized === null) {
      setBeneficiaryRates(prev => prev.map((r, i) => i === rowIndex ? { ...r, value: "" } : r));
      setBeneficiaryErrors(prev => prev.map((e, i) => i === rowIndex ? { ...e, value: "" } : e));
      return;
    }
    const numeric = sanitizeNumberInput(sanitized);
    let adjusted = numeric;
    if (adjusted < 1) adjusted = "";
    const mode = beneficiaryRates[rowIndex].mode;
    if (mode === "percentage" && adjusted !== "" && adjusted > 100) adjusted = 100;
    setBeneficiaryRates(prev => prev.map((r, i) => i === rowIndex ? { ...r, value: adjusted } : r));
    setBeneficiaryErrors(prev => prev.map((e, i) => i === rowIndex ? {
      ...e,
      value: validateBeneficiaryRow({ ...beneficiaryRates[rowIndex], value: adjusted })
    } : e));
  };

  const hasChanges = () => {
    try {
      const normalizeRates = (rates) => {
        return rates
          .filter(r => r.value && r.value !== "" && r.value !== null && r.value !== undefined)
          .map(r => ({
            key: r.key,
            mode: r.mode,
            value: String(r.value)
          }))
          .sort((a, b) => {
            if (a.key === "Employee") return -1;
            if (b.key === "Employee") return 1;
            return a.key.localeCompare(b.key);
          });
      };

      const current = normalizeRates(beneficiaryRates);
      const initial = normalizeRates(initialBeneficiaryRates);

      if (current.length !== initial.length) {
        return true;
      }

      for (let i = 0; i < current.length; i++) {
        if (current[i].key !== initial[i].key ||
          current[i].mode !== initial[i].mode ||
          current[i].value !== initial[i].value) {
          return true;
        }
      }

      return false;
    } catch (error) {
      return true;
    }
  };

  const hasRequiredFields = () => {
    const employeeRow = beneficiaryRates.find(r => r.key === "Employee");
    if (!employeeRow) {
      return false;
    }

    const hasEmployeeValue = employeeRow.value &&
      employeeRow.value !== "" &&
      employeeRow.value !== null &&
      employeeRow.value !== undefined &&
      Number(employeeRow.value) > 0;

    const customBeneficiaries = beneficiaryRates.filter(r => r.key !== "Employee");
    for (const row of customBeneficiaries) {
      const hasName = row.key && row.key.trim() !== "";
      const hasValue = row.value &&
        row.value !== "" &&
        row.value !== null &&
        row.value !== undefined &&
        Number(row.value) > 0;

      if (hasName && !hasValue) {
        return false;
      }
      if (!hasName && hasValue) {
        return false;
      }
    }

    return hasEmployeeValue;
  };

  const isUpdateMode = () => {
    const hasInitialData = initialBeneficiaryRates.some(r =>
      r.value && r.value !== "" && r.value !== null && r.value !== undefined
    );
    return hasInitialData;
  };

  const isSaveButtonEnabled = () => {
    if (isSavingCredits) {
      return false;
    }

    if (isUpdateMode()) {
      return hasChanges();
    } else {
      return hasRequiredFields();
    }
  };

  useEffect(() => {
    if (beneficiaryModalOpen) {
      const sanitizeValue = (val) => {
        if (val === null || val === undefined || val === "") return "";
        const num = Number(val);
        if (Number.isNaN(num)) return "";
        if (num < 1) return "";
        return num;
      };

      const sanitizeMode = (mode) => (mode === "percentage" ? "percentage" : "amount");

      const fetchBeneficiaries = async () => {
        try {
          const creditTypeName = selectedCreditType || selectedCreditForBeneficiary?.type || "";
          const creditTypeObj = creditsType?.find(ct => ct.name === creditTypeName);
          const creditTypeId = creditTypeObj?._id || selectedCreditForBeneficiary?._id;

          if (creditTypeId && handleGetCreditBeneficiaries) {
            const result = await handleGetCreditBeneficiaries(creditTypeId);

            let beneficiaries = null;
            let recordId = null;

            if (result) {
              if (result._id) {
                recordId = result._id;
              } else if (result.data && result.data._id) {
                recordId = result.data._id;
              }

              if (result.data && result.data.beneficiaries && Array.isArray(result.data.beneficiaries)) {
                beneficiaries = result.data.beneficiaries;
              }
              else if (result.beneficiaries && Array.isArray(result.beneficiaries)) {
                beneficiaries = result.beneficiaries;
              }
              else if (Array.isArray(result)) {
                beneficiaries = result;
              }
            }

            if (recordId) {
              setExistingCreditBeneficiaryId(recordId);
            } else {
              setExistingCreditBeneficiaryId(null);
            }

            if (beneficiaries && beneficiaries.length > 0) {
              const rates = beneficiaries.map((ben, index) => ({
                id: `beneficiary-${index}`,
                key: ben.beneficiary_type || ben.beneficiaryType || "",
                mode: ben.rateType || ben.rate_type || "amount",
                value: sanitizeValue(ben.value),
                isEditable: (ben.beneficiary_type !== "Employee" && ben.beneficiaryType !== "Employee")
              }));

              const employeeIndex = rates.findIndex(r => r.key === "Employee");
              if (employeeIndex > 0) {
                const employee = rates[employeeIndex];
                rates.splice(employeeIndex, 1);
                rates.unshift(employee);
              } else if (employeeIndex === -1) {
                rates.unshift({
                  id: "employee-0",
                  key: "Employee",
                  mode: "amount",
                  value: "",
                  isEditable: false,
                });
              }

              setBeneficiaryRates(rates);
              setInitialBeneficiaryRates(JSON.parse(JSON.stringify(rates))); // Deep copy for comparison
              setBeneficiaryErrors(rates.map(() => ({ value: "", type: "" })));
              return;
            }
          }
        } catch (error) {
          console.error("Error fetching beneficiaries:", error);
        }

        const initialRates = [
          {
            id: "employee-0",
            key: "Employee",
            mode: selectedCreditForBeneficiary
              ? sanitizeMode(selectedCreditForBeneficiary.emp_amount_type || selectedCreditForBeneficiary.employee_amt_type || "amount")
              : "amount",
            value: selectedCreditForBeneficiary ? sanitizeValue(selectedCreditForBeneficiary.amount) : "",
            isEditable: false,
          },
        ];

        setBeneficiaryRates(initialRates);
        setInitialBeneficiaryRates(JSON.parse(JSON.stringify(initialRates))); // Deep copy for comparison
        setBeneficiaryErrors(initialRates.map(() => ({ value: "", type: "" })));
        setExistingCreditBeneficiaryId(null); // Reset ID if no data found
      };

      fetchBeneficiaries();
    } else if (!beneficiaryModalOpen) {
      const emptyRates = [
        { id: "employee-0", key: "Employee", mode: "amount", value: "", isEditable: false },
      ];
      setBeneficiaryRates(emptyRates);
      setInitialBeneficiaryRates(JSON.parse(JSON.stringify(emptyRates))); // Deep copy for comparison
      setBeneficiaryErrors([
        { value: "", type: "" },
      ]);
      setExistingCreditBeneficiaryId(null); // Reset ID when modal closes
    }
  }, [beneficiaryModalOpen, selectedCreditForBeneficiary, selectedCreditType, creditsType, handleGetCreditBeneficiaries]);

  useEffect(() => {
    if (credits && Array.isArray(credits) && credits.length > 0) {
      setCredit(credits);
    }
  }, [credits]);

  // Handle click outside to revert changes
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Check if click is outside any editing row
      const clickedElement = event.target;
      const isClickOnSaveButton = clickedElement.closest('button')?.textContent?.includes('Save');
      const isClickInEditingRow = clickedElement.closest('[data-row-id]');
      const isClickOnInput = clickedElement.closest('input') || clickedElement.closest('[role="combobox"]') || clickedElement.closest('[role="listbox"]') || clickedElement.closest('[role="button"]');

      // If save button was clicked, don't revert
      if (isClickOnSaveButton) {
        saveButtonClickedRef.current = true;
        setTimeout(() => {
          saveButtonClickedRef.current = false;
        }, 300);
        return;
      }

      // If click is outside editing rows and not on any input/select/button, revert all editing rows
      if (!isClickInEditingRow && !isClickOnInput && Object.keys(editingRows).length > 0) {
        // Revert all editing rows
        Object.keys(editingRows).forEach(rowId => {
          setEditedValues(prev => {
            const updated = { ...prev };
            delete updated[rowId];
            return updated;
          });
          setEditingRows(prev => {
            const updated = { ...prev };
            delete updated[rowId];
            return updated;
          });
          setOriginalValues(prev => {
            const updated = { ...prev };
            delete updated[rowId];
            return updated;
          });
        });
      }
    };

    // Add event listener if there are editing rows
    if (Object.keys(editingRows).length > 0) {
      document.addEventListener('mousedown', handleClickOutside);

      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [editingRows]);

  useEffect(() => {
    if (beneficiaryModalOpen) return;

    setChangedRows({});
    setFrequencyChanged(false);

    const sanitizeValue = (val) => {
      if (val === null || val === undefined || val === "") return "";
      const num = Number(val);
      if (Number.isNaN(num)) return "";
      if (num < 1) return "";
      return num;
    };

    const sanitizeMode = (mode) => (mode === "percentage" ? "percentage" : "amount");

    if (credits && Array.isArray(credits) && credits.length > 0) {
      const mappedCredits = credits.map(credit => ({
        ...credit,
        employee_amt_type: credit.emp_amount_type || credit.employee_amt_type || "amount",
        spouse_amt_type: credit.spouse_amt_type || "amount"
      }));
      setCredit(mappedCredits);

      const activeCredit =
        mappedCredits.find(item => item.type === selectedCreditType) ||
        mappedCredits[0];

      if (activeCredit) {
        setBeneficiaryRates(prevRates =>
          prevRates.map((row) => {
            if (row.key === "Employee") {
              return {
                ...row,
                mode: sanitizeMode(activeCredit.emp_amount_type || activeCredit.employee_amt_type),
                value: sanitizeValue(activeCredit.amount),
                isEditable: false,
              };
            }
            // For custom types, preserve the existing dataY
            return row;
          })
        );

        if (typeof activeCredit.linked_to_contributions === "boolean") {
          setContribution(activeCredit.linked_to_contributions);
          setSelectedContributionType(activeCredit.linked_to_contributions ? (activeCredit.linked_contributions || "") : "");
        }

        if (activeCredit.frequency && typeof handleFrequencyChange === "function") {
          handleFrequencyChange(activeCredit.frequency);
        }
      }
    } else {
      setCredit([{
        type: "",
        description: "",
        amount: "",
        start: "",
        end: "",
        includes_beneficiary: false,
        employeeCont: { type: "" },
        spouse_beneficiary_type: "",
        spouse_amt: "",
        spouse_amt_type: "",
        additional_beneficiary_amt: "",
        additional_beneficiary_amt_type: "",
        linked_to_contributions: false,
        linked_contributions: ""
      }]);
      setContribution(false);
      setSelectedContributionType("");
      setBeneficiaryRates([
        { id: "employee-0", key: "Employee", mode: "amount", value: "", isEditable: false },
      ]);
      setBeneficiaryErrors([
        { value: "", type: "" },
      ]);
    }
  }, [credits, selectedCreditType, beneficiaryModalOpen]);

  const handleSave = () => {
    if (addFY) {
      handleAddTaxYear(addStart, addEnd);
      setOpen(false);
      setAddFY(false);
    }
  };

  const handleDeleteItem = () => {
    if (creditToDelete) {
      const selectedType = credits.find(ct => ct.type === creditToDelete);
      if (selectedType?._id) {
        deleteCreditType(selectedType._id, () => {
          setSelectedCreditType("");
        });
      }
    }
    setOpens(false);
    setCreditToDelete(null);
  };

  const handleTaxYearChange = async (event) => {
    const selectedYearId = event.target.value;
    setSelectedTaxYear(selectedYearId);

    if (selectedYearId && taxCountry) {
      await getYearTaxTable(selectedYearId, taxCountry);
    }

    setContribution(false);
    setSelectedContributionType("");
    setChangedRows({});
    setFrequencyChanged(false);

    const selectedFY = taxPeriod?.find((p) => p._id === selectedYearId);
    if (selectedFY) {
      const startFormatted = dayjs(selectedFY.start).format("DD-MM-YYYY");
      const endFormatted = dayjs(selectedFY.end).format("DD-MM-YYYY");

      if (getCreditsTypeByDateRange && taxCountry) {
        await getCreditsTypeByDateRange(taxCountry, startFormatted, endFormatted);
      }

      if (selectedCreditType) {
        const start = dayjs(selectedFY.start).format("YYYY-MM-DD");
        const end = dayjs(selectedFY.end).format("YYYY-MM-DD");
        // getCredits(start, end, selectedCreditType);
      }
    }
  };

  const onModalConfirm = () => {
    if (from === "edit") {
      updateCreditType({
        name: editCreditData?.type || editCreditData?.name || "",
        type: editCreditData?.type || editCreditData?.name || "",
        country: taxCountry || "",
        contribution: contribution,
        contributionType: contribution ? selectedContributionType : "",
        _id: editCreditData?._id
      });

      setCredit(prev => {
        const updated = [...prev];
        if (editCredit !== null && updated[editCredit]) {
          updated[editCredit] = {
            ...updated[editCredit],
            ...editCreditData,
            linked_to_contributions: contribution,
            linked_contributions: contribution ? selectedContributionType : ""
          };
        }
        return updated;
      });
    } else {
      handleAddCreditType(newCreditType, taxCountry);
    }

    setCreditsOpen(false);
    setNewCreditType("");
    setContribution(false);
    setSelectedContributionType("");
    setFrom("create");
    setEditCredit(null);
    setEditCreditData(null);
  };

  return (
    <>
      <Box sx={style.headerBox}>
        <Typography sx={style.inputCreditTitle}>Credits Management</Typography>
      </Box>
      <Box sx={style.gridbox}>
        <Grid container>
          <Grid item xs={12} lg={12}>
            <Box sx={style.fyFrequencyBox}>
              <Typography sx={style.inputText}>FY</Typography>
              <Select
                value={selectedTaxYear}
                onChange={handleTaxYearChange}
                name="taxyear"
                sx={style.selectFY}
                displayEmpty
                renderValue={(selected) =>
                  selected
                    ? taxPeriod?.find((item) => item._id === selected)
                      ? `${dayjs(taxPeriod.find((item) => item._id === selected).start).format(getDateFormat())} - ${dayjs(taxPeriod.find((item) => item._id === selected).end).format(getDateFormat())}`
                      : "Select FY"
                    : <span style={{ color: "#aaa" }}>Select FY</span>
                }
              >
                <MenuItem value="" disabled sx={style.dropDownMenu}>Select FY</MenuItem>
                {taxPeriod?.map((item) => (
                  <MenuItem key={item._id} sx={style.dropDownMenu} value={item._id}>
                    {dayjs(item.start).format(getDateFormat())} - {dayjs(item.end).format(getDateFormat())}
                  </MenuItem>
                ))}
              </Select>
              <Typography sx={style.inputText}>Frequency</Typography>
              <Select
                value={frequency}
                onChange={(e) => {
                  handleFrequencyChange(e.target.value);
                  setFrequencyChanged(true);
                }}
                sx={style.selectFrequency}
                displayEmpty
                renderValue={(selected) => selected ? (selected) : (<span style={{ color: "#aaa" }}>Select Frequency</span>)}
              >
                <MenuItem value="Monthly">Monthly</MenuItem>
                <MenuItem value="Fortnightly">Fortnightly</MenuItem>
                <MenuItem value="Weekly">Weekly</MenuItem>
                <MenuItem value="Custom">Custom</MenuItem>
              </Select>
            </Box>
          </Grid>
        </Grid>

        <Box sx={style.tableSectionWrapper}>
          <Box sx={style.tableCard}>
            <Box sx={style.tableHeader}>
              <Box sx={style.tableHeaderCell}>Credit Name</Box>
              <Box sx={style.tableHeaderCell}>Contribution</Box>
              <Box sx={style.tableHeaderCellCenter}>Beneficiaries</Box>
              {canEditCredits && (
                <Box sx={style.tableHeaderCellDelete}>
                  Delete
                </Box>
              )}            </Box>
            {[...(creditsType || []), ...localNewCredits].length > 0 ? (
              [...(creditsType || []), ...localNewCredits].map((item, idx) => {
                const isLocalNew = !item._id && item.tempId;
                const contributionText = item?.linked_to_contributions || item?.linked_contributions
                  ? (contributionType.find(ct => ct._id === (item.linked_to_contributions || item.linked_contributions))?.name || "—")
                  : "—";

                return (
                  <Box key={item._id || item.tempId || idx} sx={style.tableRow} data-row-id={item._id}>
                    {isLocalNew ? (
                      <>
                        <Box sx={style.tableCell}>
                          <TextField
                            disabled={!canEditCredits}
                            value={item.name || ""}
                            onChange={(e) => {
                              setLocalNewCredits(prev =>
                                prev.map(credit =>
                                  credit.tempId === item.tempId
                                    ? { ...credit, name: e.target.value }
                                    : credit
                                )
                              );
                            }}
                            placeholder="Enter credit name"
                            size="small"
                            sx={style.textFieldSmall}
                          />
                        </Box>
                        <Box sx={style.tableCell}>
                          <Select
                            disabled={!canEditCredits}
                            value={item.linked_contributions || ""}
                            onChange={(e) => {
                              setLocalNewCredits(prev =>
                                prev.map(credit =>
                                  credit.tempId === item.tempId
                                    ? { ...credit, linked_contributions: e.target.value }
                                    : credit
                                )
                              );
                            }}
                            size="small"
                            displayEmpty
                            sx={style.selectSmall}
                            renderValue={(selected) =>
                              selected
                                ? (contributionType.find(ct => ct._id === selected)?.name || "Select Contribution")
                                : <span style={{ color: "#999" }}>Select Contribution</span>
                            }
                          >
                            {contributionType?.map((ct, idx) => (
                              <MenuItem sx={style.dropDownMenu} key={idx} value={ct._id}>
                                {ct.name}
                              </MenuItem>
                            ))}
                          </Select>
                        </Box>
                        <Box sx={style.tableCell}></Box>
                        <Box sx={style.tableCellActions}>
                          <Button
                            variant="contained"
                            color="primary"
                            size="small"
                            disabled={!item.name || !item.linked_contributions || !selectedTaxYear || !frequency}
                            onClick={async () => {
                              if (!item.name || !item.linked_contributions) {
                                return;
                              }



                              try {
                                // Get start and end dates from selected tax year
                                const selectedFY = taxPeriod?.find((p) => p._id === selectedTaxYear);

                                const startDate = dayjs(selectedFY.start).format("YYYY-MM-DD");
                                const endDate = dayjs(selectedFY.end).format("YYYY-MM-DD");

                                await handleAddCreditType(
                                  item.name,
                                  taxCountry || "India",
                                  false,
                                  true,
                                  item.linked_contributions,
                                  startDate,
                                  endDate,
                                  frequency
                                );
                                setLocalNewCredits(prev => prev.filter(credit => credit.tempId !== item.tempId));
                                if (getAllCreditsList) {
                                  await getAllCreditsList();
                                }
                              } catch (error) {
                                console.error("Error adding credit:", error);
                              }
                            }}
                            sx={style.saveButton}
                          >
                            Save
                          </Button>
                          <IconButton
                            size="small"
                            onClick={() => {
                              setLocalNewCredits(prev => prev.filter(credit => credit.tempId !== item.tempId));
                            }}
                            sx={style.deleteIconButton}
                          >
                            <DeleteOutlineIcon sx={style.deleteIconStyle} />
                          </IconButton>
                        </Box>
                      </>
                    ) : (
                      <>
                        <Box sx={style.tableCell}>
                          <TextField
                            disabled={!canEditCredits}
                            value={editedValues[item._id]?.name !== undefined ? editedValues[item._id].name : item.name || ""}
                            onFocus={() => {
                              // Store original values when user starts editing
                              if (!originalValues[item._id]) {
                                setOriginalValues(prev => ({
                                  ...prev,
                                  [item._id]: {
                                    name: item.name || "",
                                    linked_contributions: item.linked_to_contributions || item.linked_contributions || ""
                                  }
                                }));
                              }
                            }}
                            onChange={(e) => {
                              const newName = e.target.value;
                              setEditedValues(prev => ({
                                ...prev,
                                [item._id]: {
                                  ...prev[item._id],
                                  name: newName
                                }
                              }));
                              // Mark row as edited
                              setEditingRows(prev => ({
                                ...prev,
                                [item._id]: true
                              }));
                            }}
                            placeholder="Enter credit name"
                            size="small"
                            sx={style.textFieldSmall}
                          />
                        </Box>
                        <Box sx={style.tableCell}>
                          <Select
                            disabled={!canEditCredits}
                            value={editedValues[item._id]?.linked_contributions !== undefined
                              ? editedValues[item._id].linked_contributions
                              : item.linked_to_contributions || item.linked_contributions || ""}
                            onOpen={() => {
                              // Store original values when user opens dropdown
                              if (!originalValues[item._id]) {
                                setOriginalValues(prev => ({
                                  ...prev,
                                  [item._id]: {
                                    name: item.name || "",
                                    linked_contributions: item.linked_to_contributions || item.linked_contributions || ""
                                  }
                                }));
                              }
                            }}
                            onChange={(e) => {
                              const newContributionId = e.target.value;
                              setEditedValues(prev => ({
                                ...prev,
                                [item._id]: {
                                  ...prev[item._id],
                                  linked_contributions: newContributionId
                                }
                              }));
                              // Mark row as edited
                              setEditingRows(prev => ({
                                ...prev,
                                [item._id]: true
                              }));
                            }}
                            size="small"
                            displayEmpty
                            sx={style.selectSmall}
                            renderValue={(selected) =>
                              selected
                                ? (contributionType.find(ct => ct._id === selected)?.name || "Select Contribution")
                                : <span style={{ color: "#999" }}>Select Contribution</span>
                            }
                          >
                            <MenuItem value="" sx={style.dropDownMenu}>
                              <span style={{ color: "#999" }}>Select Contribution</span>
                            </MenuItem>
                            {contributionType?.map((ct, idx) => (
                              <MenuItem sx={style.dropDownMenu} key={idx} value={ct._id}>
                                {ct.name}
                              </MenuItem>
                            ))}
                          </Select>
                        </Box>
                        <Box sx={style.tableCellBeneficiaries}>
                          {item._id ? (
                            <IconButton
                              size="small"
                              onClick={() => {
                                const creditRecord = credit.find(c => c.type === item.name);
                                if (creditRecord) {
                                  setSelectedCreditForBeneficiary(creditRecord);
                                  setSelectedCreditType(item.name);
                                  setBeneficiaryModalOpen(true);
                                } else {
                                  setSelectedCreditForBeneficiary({
                                    type: item.name,
                                    _id: null,
                                    includes_beneficiary: item.includes_beneficiary || false
                                  });
                                  setSelectedCreditType(item.name);
                                  setBeneficiaryModalOpen(true);
                                }
                              }}
                              sx={style.beneficiaryIconButton}
                            >
                              <PeopleAltIcon sx={style.beneficiaryIcon} />
                            </IconButton>
                          ) : (
                            <Typography sx={style.emptyText}>—</Typography>
                          )}
                        </Box>
                        <Box sx={style.tableCellDelete}>
                          {editingRows[item._id] && (
                            <Button
                              variant="outlined"
                              size="small"
                              onClick={async (e) => {
                                e.stopPropagation();
                                saveButtonClickedRef.current = true; // Mark that save button was clicked
                                const edited = editedValues[item._id];
                                if (item._id && updateCreditType && edited) {
                                  const newName = edited.name !== undefined ? edited.name : item.name;
                                  const newContribution = edited.linked_contributions !== undefined
                                    ? edited.linked_contributions
                                    : item.linked_to_contributions || item.linked_contributions || "";

                                  await updateCreditType(
                                    item._id,
                                    item.country || taxCountry || "India",
                                    true,
                                    newContribution,
                                    newName
                                  );
                                  // Clear editing state
                                  setEditingRows(prev => {
                                    const updated = { ...prev };
                                    delete updated[item._id];
                                    return updated;
                                  });
                                  setEditedValues(prev => {
                                    const updated = { ...prev };
                                    delete updated[item._id];
                                    return updated;
                                  });
                                  setOriginalValues(prev => {
                                    const updated = { ...prev };
                                    delete updated[item._id];
                                    return updated;
                                  });
                                  // Refresh list after update
                                  if (getAllCreditsList) {
                                    await getAllCreditsList();
                                  }
                                }
                              }}
                              sx={{
                                ...style.saveButton,
                                borderRadius: "20px",
                                textTransform: "none",
                                backgroundColor: "#f5f5f5",
                                color: "#666",
                                borderColor: "#ddd",
                                minWidth: "70px",
                                height: "32px",
                                fontSize: "12px",
                                mr: 1,
                                "&:hover": {
                                  backgroundColor: "#e8e8e8",
                                  borderColor: "#bbb"
                                }
                              }}
                            >
                              Save
                            </Button>
                          )}
                          {canEditCredits && (
                            <IconButton
                              size="small"
                              onClick={() => {
                                if (item._id && deleteCreditById) {
                                  deleteCreditById(item._id);
                                } else if (item.name) {
                                  setCreditToDelete(item.name);
                                  setOpens(true);
                                }
                              }}
                              sx={style.deleteIconButton}
                            >
                              <DeleteOutlineIcon sx={style.deleteIconStyle} />
                            </IconButton>
                          )}
                        </Box>
                      </>
                    )}
                  </Box>
                );
              })
            ) : (
              <Box sx={style.tableRow}>
                <Box sx={style.emptyRow}>
                  No Data
                </Box>
              </Box>
            )}
            {canEditCredits && (
              <Box sx={style.addNewCreditBox}>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<AddIcon />}
                  onClick={() => {
                    const tempId = `temp_${Date.now()}_${Math.random()}`;
                    setLocalNewCredits(prev => [...prev, {
                      tempId,
                      name: "",
                      linked_contributions: "",
                      includes_beneficiary: false
                    }]);
                  }}
                  sx={style.addNewCreditButton}
                >
                  Add New Credit
                </Button>
              </Box>
            )}
          </Box>
        </Box>
      </Box>

      <Modal
        open={creditsOpen}
        setOpen={setCreditsOpen}
        btnText={from === "edit" ? "Save Changes" : "Add New"}
        title={from === "edit" ? "Edit Credit Type" : "Add Credit Type"}
        handleCheck={onModalConfirm}
      >
        <Box>
          <TextField
            sx={style.modalInput}
            name="Credits"
            value={from === "edit" ? editCreditData?.type || "" : newCreditType}
            onChange={(e) => {
              if (from === "edit") {
                setEditCreditData({
                  ...editCreditData,
                  type: e.target.value
                });
              } else {
                setNewCreditType(e.target.value);
              }
            }}
            placeholder="Enter credit type name"
            fullWidth
          />
        </Box>
      </Modal>
      <Modal
        open={opens}
        setOpen={setOpens}
        deletetText={"Delete"}
        cancelText={"Cancel"}
        title={"Delete Credit Type"}
        handleDelete={handleDeleteItem}
      >
        <Box>
          <Typography>Are you sure you want to delete?</Typography>
        </Box>
      </Modal>

      <Modal
        open={open}
        setOpen={setOpen}
        disabled={addFY ? !addStart || !addEnd : false}
        btnText={"Add Financial Year"}
        title={"Add Financial Year"}
        handleCheck={handleSave}
      >
        <Box sx={style.modalGrid}>
          <>
            <Box sx={style.inputBoxs}>
              <Typography sx={style.inputTexts}>From</Typography>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <Box>
                  <DatePicker
                    value={addStart ? dayjs(addStart) : null}
                    slotProps={{
                      field: { readOnly: true },
                    }}
                    sx={style.calenderInput}
                    onChange={(newValue) => setAddStart(newValue)}
                  />
                </Box>
              </LocalizationProvider>
            </Box>
            <Box sx={style.inputBoxs}>
              <Typography sx={style.inputTexts}>To</Typography>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <Box>
                  <DatePicker
                    value={addEnd ? dayjs(addEnd) : null}
                    slotProps={{
                      field: { readOnly: true },
                    }}
                    sx={style.calenderInput}
                    onChange={(newValue) => setAddEnd(newValue)}
                  />
                </Box>
              </LocalizationProvider>
            </Box>
          </>
        </Box>
      </Modal>

      <Modal
        open={beneficiaryModalOpen}
        setOpen={setBeneficiaryModalOpen}
        title="Rate per Beneficiary Type"
        btnText="Save"
        disabled={!isSaveButtonEnabled()}
        modalStyle={{
          "& .MuiPaper-root": {
            borderRadius: "20px",
            width: { xs: "95vw", sm: "90vw", md: "700px", lg: "800px" },
            maxWidth: "90vw",
            maxHeight: "90vh",
            overflow: "auto"
          }
        }}
        handleCheck={async (e) => {
          // Prevent form submission if event is provided
          if (e && e.preventDefault) {
            e.preventDefault();
          }


          const beneErrors = computeBeneficiaryErrors();
          setBeneficiaryErrors(beneErrors);

          const isValid = isFormValid();

          if (!isValid) {
            return;
          }

          try {
            const creditTypeName = selectedCreditType || selectedCreditForBeneficiary?.type || "";

            const creditTypeObj = creditsType?.find(ct => ct.name === creditTypeName);
            const creditTypeId = creditTypeObj?._id || selectedCreditForBeneficiary?._id || creditTypeName;

            const beneficiaries = beneficiaryRates
              .filter(r => r.value && r.value !== "" && r.value !== null && r.value !== undefined)
              .map(r => ({
                beneficiary_type: r.key,
                rateType: r.mode,
                value: Number(r.value)
              }));


            if (!beneficiaries || beneficiaries.length === 0) {
              return;
            }

            const payload = {
              credit_type: creditTypeId,
              country: taxCountry || "India",
              beneficiaries: beneficiaries
            };

            setIsSavingCredits(true);

            if (handlePostCreditBeneficiaries) {
              await handlePostCreditBeneficiaries(payload, existingCreditBeneficiaryId);
            } else {
              await handleCreditsData(payload);
            }

            if (getAllCreditsList) {
              setTimeout(() => {
                getAllCreditsList();
              }, 500);
            }
            setBeneficiaryModalOpen(false);
          } catch (e) {
            // no-op; alerts handled upstream if needed
          } finally {
            setIsSavingCredits(false);
            setFrequencyChanged(false);
            setChangedRows({});
          }
        }}
      >
        <Box sx={{ width: "100%" }}>
          <Box sx={style.tableSectionWrapper}>
            <Box sx={style.tableCard}>
              <Box sx={style.tableHeader}>
                <Box sx={style.thType}>Type</Box>
                <Box sx={style.thMode}>Mode</Box>
                <Box sx={style.thValue}>Value</Box>
                <Box sx={style.tableCellDeleteAction}></Box>
              </Box>
              {beneficiaryRates.map((row, i) => {
                const actualIndex = i;
                const isEditable = row.isEditable !== false && row.key !== "Employee";
                return (
                  <Box key={row.id || `row-${i}`} sx={style.tableRow}>
                    <Box sx={style.tdType}>
                      {isEditable ? (
                        <TextField
                          value={row.key}
                          disabled={!canEditCredits}
                          onChange={(e) => {
                            setBeneficiaryRates(prev => prev.map((r, idx) =>
                              idx === actualIndex ? { ...r, key: e.target.value } : r
                            ));
                            if (beneficiaryErrors[actualIndex]?.type) {
                              setBeneficiaryErrors(prev => prev.map((err, idx) =>
                                idx === actualIndex ? { ...err, type: "" } : err
                              ));
                            }
                          }}
                          placeholder="Enter type"
                          size="small"
                          sx={style.modalInput}
                          error={!!beneficiaryErrors[actualIndex]?.type}
                          helperText={beneficiaryErrors[actualIndex]?.type || ""}
                          fullWidth
                        />
                      ) : (
                        <Typography sx={{ fontSize: "14px", py: 1 }}>{row.key}</Typography>
                      )}
                    </Box>
                    <Box
                      sx={{
                        pointerEvents: canEditCredits ? "auto" : "none",
                        opacity: canEditCredits ? 1 : 0.7,
                      }}
                    >
                      <CustomizedRadio
                        value={row.mode}
                        radioLabel={[
                          { label: "Amount", value: "amount" },
                          { label: "Percentage", value: "percentage" }
                        ]}
                        handleChange={(val) => handleBeneficiaryMode(actualIndex, val)}
                      />
                    </Box>
                    <Box sx={style.tdValue}>
                      <TextField
                        value={row.value}
                        disabled={!canEditCredits}
                        onChange={(e) => handleBeneficiaryValue(actualIndex, e.target.value)}
                        placeholder="0.00"
                        type="number"
                        size="small"
                        sx={{ ...style.modalInput, ...style.valueInput }}
                        onKeyDown={(e) => { if (["e", "E", "+", "-"].includes(e.key)) { e.preventDefault(); } }}
                        error={!!beneficiaryErrors[actualIndex]?.value}
                        helperText={beneficiaryErrors[actualIndex]?.value || ""}
                        fullWidth
                      />
                    </Box>
                    <Box sx={style.tableCellDeleteAction}>
                      {canEditCredits && row.key !== "Employee" && (
                        <IconButton
                          size="small"
                          onClick={() => {
                            setBeneficiaryRates(prev =>
                              prev.filter((_, idx) => idx !== actualIndex)
                            );
                            setBeneficiaryErrors(prev =>
                              prev.filter((_, idx) => idx !== actualIndex)
                            );
                          }}
                          sx={style.deleteIconButton}
                        >
                          <DeleteOutlineIcon sx={style.deleteIconStyle} />
                        </IconButton>
                      )}
                    </Box>
                  </Box>
                );
              })}
            </Box>
          </Box>
          {canEditCredits && (
            <Box sx={style.addBeneficiaryBox}>
              <Button
                variant="outlined"
                onClick={() => {
                  const newId = `beneficiary-${Date.now()}-${Math.random()}`;
                  setBeneficiaryRates(prev => [
                    ...prev,
                    { id: newId, key: "", mode: "amount", value: "", isEditable: true }
                  ]);
                  setBeneficiaryErrors(prev => [
                    ...prev,
                    { value: "", type: "" }
                  ]);
                }}
                sx={style.addBeneficiaryButton}
              >
                + Add Beneficiary Type
              </Button>
            </Box>
          )}
        </Box>
      </Modal>
    </>
  );
};

export default CreditsPage;
